import { isNaN, toString, toNumber, round, isArray } from 'lodash-es';
import { arr_count, arr_first, blank, transform } from './common';

export const toFormattedNumber = (value: any, opts: {
  maxDecimalPlaces?: number,
  currency?: boolean,
} = {}): number => {
  if (isNaN(value) || value === 'NaN') {
    return 0;
  }

  value = toString(value).trim();

  const places = opts.maxDecimalPlaces || ((opts.currency) ? _kMaxDecimalPlacesForCurrency : _kMaxDecimalPlacesForNumbers);
  const softLimit = new RegExp(`^(?:-?(?:\\d*)?(?:\\.\\d{1,${places}})?)$`, 'i');
  const hardLimit = new RegExp(`^(?:-?(?:\\d*\\.)?\\d{${places}})$`, 'i');

  if (softLimit.test(value)) {
    return toNumber(value);
  } else if (/^(?:\-?[\d]+)?(?:\.[\d]*)?$/.test(value) && !hardLimit.test(value)) {
    return toNumber(round(toNumber(value), places).toFixed(places));
  }

  return 0;
}

const _kMaxDecimalPlacesForNumbers = 3;
const _kMaxDecimalPlacesForCurrency = 2;

/// computes the markup percentage on a given unit price and unit cost
export const markup = (opts: {
  unit_price?: number,
  unit_cost?: number,
} = {}): number => {
  opts = Object.assign({
    unit_price: 0,
    unit_cost: 0,
  }, opts);

  if (opts.unit_cost === 0) {
    return 0;
  }

  return toFormattedNumber(
    (100 * ((opts.unit_price - opts.unit_cost) / opts.unit_cost))
  );
}

/// computes the tax on a given price
export const tax = (rate: number, opts: {
  price?: number,
} = {}): number => {
  opts = Object.assign({
    price: 0,
  }, opts);

  if (rate === 0 || opts.price === 0) {
    return opts.price;
  }

  return toFormattedNumber((rate / 100) * opts.price, {
    currency: true,
    maxDecimalPlaces: 4,
  });
}

export const to_numeric = (value: any): number => {
  if (blank(value)) {
    return 0;
  }

  const transformed = toNumber(value);

  if (isNaN(transformed)) {
    return 0;
  }

  return transformed;
};

export const sum = (values: any | any[]): number => {
  if (blank(values)) {
    return 0;
  }

  if (! isArray(values)) {
    values = [values];
  }

  let total = 0;

  for (let addend of values) {
    total += to_numeric(addend);
  }

  return total;
}

export const multiply = (opts: {
  multiplicant: any,
  multiplier: any,
}): number => {
  let multiplicant = opts.multiplicant;
  let multiplier = opts.multiplier;

  if (blank(multiplicant) || blank(multiplier)) {
    return 0;
  }

  multiplicant = to_numeric(multiplicant);
  multiplier = to_numeric(multiplier);

  return multiplicant * multiplier;
};

export const difference = (values: any | any[]): number => {
  if (blank(values)) {
    return 0;
  }

  if (! isArray(values)) {
    values = [values];
  }

  if (arr_count(values) === 1) {
    return arr_first(values);
  }

  let minuend = to_numeric(arr_first(values));

  for (let subtrahend of values.slice(1)) {
    subtrahend = to_numeric(subtrahend);

    if (isNaN(subtrahend)) {
      subtrahend = 0;
    }

    minuend -= subtrahend;
  }

  return minuend;
};

export const variance_percentage = (opts: {
  estimate: any;
  actual: any;
  negate?: boolean;
}): number => {
  opts = Object.assign({
    negate: false,
  }, opts);

  const estimate: number = to_numeric(opts.estimate);
  const actual: number = to_numeric(opts.actual);

  if (estimate === 0) {
    return 0;
  }

  const variance = ((actual - estimate) / estimate) * 100;

  if (! opts.negate) {
    return variance;
  }

  if (variance > 0) {
    return -variance;
  }

  return Math.abs(variance);
};

export const divide = (opts: {
  dividend: any,
  divisor: any,
}): number => {
  let dividend = opts.dividend;
  let divisor = opts.divisor;

  if (blank(dividend) || blank(divisor)) {
    return 0;
  }

  dividend = to_numeric(dividend);
  divisor = to_numeric(divisor);

  /// you cannot divide a value to zero or divide anything to zero
  if (divisor === 0 || dividend === 0) {
    return 0;
  }

  return dividend / divisor;
};

export const ceil_percentage = (value: any): number => {
  value = to_numeric(value);

  if (value === 0) {
    return 0;
  }

  if (value <= 100) {
    return value;
  }

  return 100;
};

export const price_from_markup = (cost: any, markup: any): number => {
  cost = to_numeric(cost);
  markup = to_numeric(markup);

  if (cost === 0) {
    return 0;
  }

  if (markup === 0) {
    return cost;
  }

  return round(cost * (1 + (markup / 100)), 4);
}

export const cost_from_markup = (price: number, markup: number): number => {
  price = to_numeric(price);
  markup = to_numeric(markup);

  if (price === 0) {
    return 0;
  }

  if (markup === 0) {
    return price;
  }

  return round(price / (1 + (markup / 100)), 4);
};
